import React, { useEffect, useState } from 'react';

/**
 * EntityLogo — the single way an account or location logo is drawn.
 *
 * HEIGHT IS THE CONTROL; WIDTH FOLLOWS THE LOGO.
 *
 * This component used to force a rigid 3:1 box on the belief that "every logo
 * the API stores is 3:1". That was true once and is not any more. The upload
 * pipeline used to call resize.Resize with BOTH dimensions fixed, which
 * STRETCHES: every square or round logo was squashed to exactly 150x50 and the
 * distortion was baked into the stored bytes. A 3:1 frame was the right frame
 * for that, because every stored logo really was 3:1.
 *
 * That was fixed (backend: "stop stretching logos on upload") — uploads now go
 * through resize.Thumbnail, which scales to fit WITHIN 150x50 and preserves the
 * aspect ratio. A square logo is now stored 50x50, a 2:1 logo 100x50. So the
 * rigid 3:1 frame became the bug: a square logo rendered at a third of its
 * possible size, marooned in bands of empty space, which reads as "the logo is
 * the wrong shape".
 *
 * So the slot now hugs the logo. Height is fixed by the caller (that is what
 * keeps rows looking even), width is auto, and the image fills the height at
 * its true proportions. Bounds:
 *
 *   minWidth  height      keeps a sane slot for placeholders and very narrow
 *                         marks, so an empty state is still visible/clickable
 *   maxWidth  height x 3  insurance against a pathologically wide logo blowing
 *                         out a nav bar. Storage fits within 150x50 but does
 *                         NOT cap the ratio — a 500x100 source lands as 150x30,
 *                         still 5:1 — so this bound is real, not theoretical.
 *                         object-fit: contain letterboxes those rather than
 *                         distorting them.
 *
 * NOTE ON OLD LOGOS: anything uploaded before the backend fix is still squashed
 * in storage. No CSS can undo that — the original pixels were discarded. Those
 * marks will keep looking stretched until they are re-uploaded, and because
 * they are exactly 3:1 they will fill this slot completely, which is the best
 * that can be done with them.
 *
 * The placeholder is React state, not a DOM node poked at from an onError
 * handler. The old approach queried `.placeholder-icon` and flipped
 * `style.display` directly; React knew nothing about it, so a re-render could
 * restore the hidden icon on top of a perfectly good logo — which is exactly
 * what the dashboard card was doing.
 */

/**
 * Widest slot a logo may occupy, as a multiple of its height.
 *
 * This is a MAXIMUM, not the shape logos have. It matches the storage box's own
 * ratio (150x50) so a full-width stored logo fits exactly, while anything
 * wider is contained rather than allowed to stretch a layout.
 */
export const LOGO_MAX_ASPECT_RATIO = 3;

export interface EntityLogoProps {
  /**
   * Image URL. Omit only when you want NO request at all — note the
   * account/location logo endpoints return a generated initials badge for
   * entities without an uploaded logo, so passing the endpoint URL is usually
   * better than omitting it.
   */
  src?: string | null;
  /**
   * Rendered height in px. Width follows the logo's own proportions, bounded
   * to [height, height x LOGO_MAX_ASPECT_RATIO].
   */
  height: number;
  /** Drawn when there is no src, or when the image fails to load. */
  placeholder?: React.ReactNode;
  alt?: string;
  /** Extra styles for the outer box (margins, radius overrides). */
  style?: React.CSSProperties;
  /**
   * Class on the outer box. Needed by callers whose responsive rules live in
   * a stylesheet rather than inline — the location dashboard hides its logo
   * entirely below a breakpoint, which cannot be expressed as a prop.
   */
  className?: string;
  title?: string;
}

export const EntityLogo: React.FC<EntityLogoProps> = ({ src, height, placeholder, alt = '', style, className, title }) => {
  const [failed, setFailed] = useState(false);

  // A new src is a new chance to succeed — without this, one failure would
  // stick to the slot as the list re-renders with different entities.
  useEffect(() => {
    setFailed(false);
  }, [src]);

  const maxWidth = Math.round(height * LOGO_MAX_ASPECT_RATIO);
  const showImage = !!src && !failed;

  return (
    <span
      title={title}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0F1115',
        borderRadius: 3,
        border: '1px solid #222',
        overflow: 'hidden',
        ...style,
        // After the spread: a caller may restyle the frame, but not change the
        // geometry. Height is fixed so rows stay even; width is left to the
        // logo so a square mark is drawn square instead of being marooned in a
        // 3:1 box.
        height,
        width: 'auto',
        minWidth: height,
        maxWidth,
        flexShrink: 0,
      }}
    >
      {showImage ? (
        <img
          src={src || undefined}
          alt={alt}
          // Fill the height, take whatever width that implies, and contain
          // inside the max — so the mark is never stretched and never cropped.
          style={{ height: '100%', width: 'auto', maxWidth: '100%', objectFit: 'contain' }}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      ) : (
        (placeholder ?? null)
      )}
    </span>
  );
};

export default EntityLogo;
